import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const STORAGE_KEY = 'cookie-consent';
const CONSENT_VERSION = 2; // Bei Änderungen an Kategorien hochzählen → neuer Consent nötig

/**
 * DSGVO-konformer Cookie-Banner
 * ------------------------------
 * - Banner blockiert NICHT die Seite (kein Dark-Pattern), zeigt aber alle relevanten Optionen
 * - "Notwendig" ist immer aktiv und nicht abwählbar (Art. 6 Abs. 1 lit. f DSGVO)
 * - "Karten" (Google Maps) lädt erst bei expliziter Einwilligung
 * - Keine Vorabauswahl bei optionalen Cookies (= echte Einwilligung)
 * - Auswahl widerrufbar via Footer-Link "Cookie-Einstellungen"
 */

const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  essential: true,
  maps: false,
  timestamp: null,
};

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
};

const writeConsent = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (_e) {
    /* SafeMode / disabled storage – ignore */
  }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setSettings(existing);
    }

    window.openCookieSettings = () => {
      setShowSettings(true);
      setVisible(true);
    };
    return () => {
      delete window.openCookieSettings;
    };
  }, []);

  const persist = useCallback((next) => {
    const finalSettings = {
      ...next,
      essential: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    writeConsent(finalSettings);
    setSettings(finalSettings);
    setVisible(false);
    setShowSettings(false);
    window.dispatchEvent(
      new CustomEvent('cookie-consent-changed', { detail: finalSettings })
    );
  }, []);

  const handleAcceptAll = () => persist({ ...settings, maps: true });
  const handleEssentialOnly = () => persist({ ...DEFAULT_CONSENT });
  const handleSave = () => persist(settings);

  const toggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Overlay nur bei Settings-Modus oder Erstauswahl auf Mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-wood-950/40 backdrop-blur-sm pointer-events-none md:hidden"
            aria-hidden="true"
          />

          <motion.aside
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-title"
            aria-describedby="cookie-desc"
            className="fixed bottom-3 left-3 right-3 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-[100]
                       bg-white/95 dark:bg-wood-900/95 backdrop-blur-md rounded-3xl shadow-wood
                       ring-1 ring-wood-200 dark:ring-wood-700 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-wood-gradient text-cream-50 flex items-center justify-center">
                  <ShieldCheckIcon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <h2
                    id="cookie-title"
                    className="font-display text-lg text-wood-900 dark:text-cream-50 leading-tight"
                  >
                    {showSettings
                      ? 'Datenschutz-Einstellungen'
                      : 'Datenschutz auf einen Blick'}
                  </h2>
                </div>
                {!showSettings && (
                  <button
                    type="button"
                    onClick={handleEssentialOnly}
                    aria-label="Nur notwendige akzeptieren und schließen"
                    className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-wood-100 dark:hover:bg-wood-800 flex items-center justify-center text-wood-500 dark:text-cream-100/70"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              <p id="cookie-desc" className="text-sm text-wood-600 dark:text-cream-100/80 leading-relaxed">
                Diese Website verwendet ausschließlich technisch notwendige Speicherobjekte
                (z.B. zur Speicherung dieser Auswahl). Externe Dienste wie Google Maps
                werden nur nach ausdrücklicher Einwilligung geladen. Mehr Informationen
                finden Sie in der{' '}
                <Link to="/datenschutz" className="underline">
                  Datenschutzerklärung
                </Link>
                .
              </p>

              {showSettings && (
                <div className="mt-5 space-y-3">
                  <Category
                    title="Technisch notwendige Cookies"
                    description="Erforderlich, um Ihre Datenschutz-Einstellungen zu speichern und die Website betriebsbereit zu halten. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO."
                    checked={true}
                    disabled
                  />
                  <Category
                    title="Karten (Google Maps)"
                    description="Lädt eine interaktive Karte auf der Kontaktseite. Beim Aktivieren werden Daten (u.a. IP-Adresse) an Google LLC, USA übertragen. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO."
                    checked={settings.maps}
                    onChange={() => toggle('maps')}
                  />
                </div>
              )}

              <div
                className={[
                  'mt-5 flex flex-col gap-2',
                  showSettings ? '' : 'sm:flex-row sm:flex-wrap',
                ].join(' ')}
              >
                {showSettings ? (
                  <>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="btn btn-primary w-full"
                    >
                      Auswahl speichern
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={handleEssentialOnly}
                        className="btn btn-ghost w-full !py-2 !text-sm"
                      >
                        Nur notwendige
                      </button>
                      <button
                        type="button"
                        onClick={handleAcceptAll}
                        className="btn btn-secondary w-full !py-2 !text-sm"
                      >
                        Alle akzeptieren
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleAcceptAll}
                      className="btn btn-primary order-1 sm:order-2 sm:flex-1"
                    >
                      Alle akzeptieren
                    </button>
                    <button
                      type="button"
                      onClick={handleEssentialOnly}
                      className="btn btn-secondary order-2 sm:order-1 sm:flex-1"
                    >
                      Nur notwendige
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSettings(true)}
                      className="btn btn-ghost order-3 !py-2 !text-sm w-full text-wood-600 dark:text-cream-100/80"
                    >
                      Einstellungen anpassen
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const Category = ({ title, description, checked, disabled, onChange }) => (
  <div className="rounded-2xl ring-1 ring-wood-200 dark:ring-wood-700 bg-cream-50 dark:bg-wood-950/50 p-4">
    <label
      className={[
        'flex items-start gap-3 cursor-pointer',
        disabled ? 'cursor-not-allowed opacity-90' : '',
      ].join(' ')}
    >
      <span className="flex-shrink-0 mt-0.5">
        <span
          className={[
            'block w-10 h-6 rounded-full transition-colors relative',
            checked ? 'bg-wood-600 dark:bg-gold-500' : 'bg-wood-200 dark:bg-wood-700',
            disabled ? 'opacity-70' : '',
          ].join(' ')}
          aria-hidden="true"
        >
          <span
            className={[
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-soft transition-transform',
              checked ? 'translate-x-4' : '',
            ].join(' ')}
          />
        </span>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className="sr-only"
          aria-label={title}
        />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-wood-900 dark:text-cream-50 text-sm">
          {title}
        </span>
        <span className="block text-xs text-wood-600 dark:text-cream-100/70 mt-1 leading-relaxed">
          {description}
        </span>
      </span>
    </label>
  </div>
);

export default CookieConsent;
